/**
 * "Atividade por serviço" on a rider record (D580): for each service the rider used (ride, delivery, food) how many requests were made,
 * completed and cancelled, what was spent, the average ticket and the first and last use — from the analytics summary, never from the
 * operational database — plus how fresh the analytics are.
 */
import { type ReactElement } from 'react';
import { type CustomerSummary } from '@vello/api-client';
import { KeyValueList, RecordSection, Text } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, type CardState } from './person-card';
import { usePersonFormat } from './person-format';

export function PersonActivityCard({ summary, reload }: { summary: CardState<CustomerSummary> | undefined; reload: () => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();

  return (
    <PersonCard
      title={t('prec.activity.title')}
      what={t('prec.what.activity')}
      state={summary}
      reload={reload}
      heldBack={t('prec.needsMarket')}
      isEmpty={(s) => s.verticals.every((x) => x.requested === 0)}
      empty={t('prec.activity.empty')}
    >
      {(s) => (
        <>
          {s.verticals
            .filter((x) => x.requested > 0)
            .map((x) => (
              <RecordSection key={x.vertical}>
                <Text as="h3" role="label">
                  {v('common.vertical', x.vertical)}
                </Text>
                <KeyValueList
                  dense
                  items={[
                    { label: t('prec.activity.requested'), value: fmt.count(x.requested), mono: true },
                    { label: t('prec.activity.completed'), value: fmt.count(x.completed), mono: true },
                    { label: t('prec.activity.cancelled'), value: x.cancelledByCustomer === null ? fmt.count(x.cancelled) : t('prec.activity.cancelled.value', { cancelled: fmt.count(x.cancelled), byCustomer: fmt.count(x.cancelledByCustomer) }), mono: true },
                    { label: t('prec.activity.spend'), value: fmt.money(x.spend), mono: true },
                    { label: t('prec.activity.ticket'), value: fmt.money(x.averageTicket), mono: true },
                    { label: t('prec.activity.first'), value: fmt.when(x.firstAt), mono: true },
                    { label: t('prec.activity.last'), value: fmt.when(x.lastAt), mono: true },
                  ]}
                />
              </RecordSection>
            ))}
          <Text as="p" role="caption" tone="secondary">
            {s.asOf !== null ? t('prec.asOf', { when: fmt.when(s.asOf) }) : t('prec.asOf.unknown')}
          </Text>
        </>
      )}
    </PersonCard>
  );
}
